import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSystemRoleStore } from "../stores/systemRoleStore";
import { SystemRoleForm } from "./SystemRoleForm";
import { RolePermissionsPanel } from "./RolePermissionsPanel";

const TABS = [
  { value: "info", label: "역할 정보" },
  { value: "permissions", label: "권한 관리" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

export function SystemRoleDrawer() {
  const drawerOpen = useSystemRoleStore((s) => s.drawerOpen);
  const editingRoleGuid = useSystemRoleStore((s) => s.editingRoleGuid);
  const closeDrawer = useSystemRoleStore((s) => s.closeDrawer);
  const setEditingRoleGuid = useSystemRoleStore((s) => s.setEditingRoleGuid);
  const [activeTab, setActiveTab] = useState<TabValue>("info");

  const isEditMode = !!editingRoleGuid;

  const handleClose = () => {
    setActiveTab("info");
    closeDrawer();
  };

  return (
    <Sheet
      open={drawerOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex flex-col overflow-hidden p-0 sm:max-w-[600px]"
      >
        <SheetHeader className="shrink-0 px-4 pt-4 pb-2">
          <SheetTitle className="text-sm">
            {isEditMode ? "시스템 역할 수정" : "시스템 역할 등록"}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {isEditMode
              ? "변경 사항은 모든 회사 복제본에 즉시 동기화됩니다."
              : "신규 역할은 같은 카테고리의 모든 회사에 자동 복제됩니다."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex shrink-0 gap-1 border-b border-gray-200 px-4">
          {TABS.map((tab) => {
            const disabled = !isEditMode && tab.value === "permissions";
            return (
              <button
                key={tab.value}
                type="button"
                disabled={disabled}
                onClick={() => setActiveTab(tab.value)}
                className={`relative px-3 py-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:text-gray-300 ${
                  activeTab === tab.value
                    ? "text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                {activeTab === tab.value && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-900" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {activeTab === "info" ? (
            <SystemRoleForm
              key={editingRoleGuid ?? "add"}
              roleGuid={editingRoleGuid}
              onSavedToEdit={(guid) => {
                setEditingRoleGuid(guid);
                setActiveTab("permissions");
              }}
            />
          ) : isEditMode && editingRoleGuid ? (
            <RolePermissionsPanel
              key={editingRoleGuid}
              roleGuid={editingRoleGuid}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
